import * as React from "react";

// NPM
import { animated } from "react-spring";

// MUI
import {
	withStyles,
	WithStyles,
	createStyles,
	Theme,
} from "@material-ui/core/styles";
import { Grid } from "@material-ui/core";

// Utility
import Utility from "../domain/classes/Utility";

// Components
import StyledSectionContainer from "../components/common/SectionContainer.Component";
import StyledDateNavigationToolbar from "../components/appointmentTimeSelection/DateNavigationToolbar.Component";
import StyledAppointmentTypeItem from "../components/appointmentTypeSelection/AppointmentTypeItem.Component";
import { AnimatedAppointmentsContainer } from "../components/appointmentTimeSelection/AppointmentsContainer.Component";

// Contexts
import { useCalendarManager } from "../contexts/CalendarManager.Context";

// Interfaces
import IAppointmentRecord from "../domain/interfaces/IAppointmentRecord";

// Classes
import Calendar from "../domain/classes/Calendar";
import IRequestResponse, {
	InitialRquestResponseState,
} from "../domain/interfaces/IRequestResponse";
import AppointmentRecord from "../domain/classes/AppointmentRecord";
import { AxiosError } from "axios";

export const styles = (theme: Theme) => createStyles({ root: {} });

export interface AppointmentTimeSelectionSectionProps {}

const AppointmentTimeSelectionSection = React.forwardRef<
	HTMLDivElement,
	AppointmentTimeSelectionSectionProps & WithStyles<typeof styles>
>(({ classes }, ref) => {
	const { dateRange, rangeSize } = useCalendarManager();

	// States
	const [appointmentRecords, setAppointmentRecords] = React.useState<
		IAppointmentRecord[]
	>([]);
	const [
		requestResponse,
		setRequestResponse,
	] = React.useState<IRequestResponse>({ ...InitialRquestResponseState });

	// Callbacks
	const fetchAppointmentsBetween = React.useCallback(
		async (fromDate: string, toDate: string) => {
			setRequestResponse((prevState: IRequestResponse) => ({
				...prevState,
				loading: true,
			}));

			try {
				const response = await AppointmentRecord.getAppointmentsBetween(
					fromDate,
					toDate
				);

				const filteredRecords = AppointmentRecord.filterRecords(
					response.data
				);

				setAppointmentRecords(filteredRecords);
			} catch (error) {
				setRequestResponse((prevState: IRequestResponse) => ({
					...prevState,
					error: (error as AxiosError).message,
				}));
			}
			setRequestResponse((prevState: IRequestResponse) => ({
				...prevState,
				loading: false,
			}));
		},
		[]
	);

	// Effects
	React.useEffect(() => {
		fetchAppointmentsBetween(dateRange[0], dateRange[rangeSize - 1]);
	}, [fetchAppointmentsBetween, dateRange]);

	return (
		<StyledSectionContainer
			ref={ref}
			className={classes.root}
			maxWidth="xl"
		>
			<StyledDateNavigationToolbar />

			<StyledAppointmentTypeItem useSelected showImg />

			<Grid container spacing={2}>
				{dateRange.map((date: string, index: number) => {
					const records = appointmentRecords.filter(
						(rec: IAppointmentRecord) =>
							Calendar.datesAreSame(rec.dateAt, date)
					);

					return (
						<Grid item xs={12} sm={6} md={4} key={index}>
							<AnimatedAppointmentsContainer
								date={date}
								apptRecords={records}
								apptRequestResponse={requestResponse}
							/>
						</Grid>
					);
				})}
			</Grid>
		</StyledSectionContainer>
	);
});

const StyledAppointmentTimeSelectionSection = withStyles(styles, {
	classNamePrefix: Utility.generateClassName(
		"AppointmentTimeSelectionSection"
	),
})(AppointmentTimeSelectionSection);

export default StyledAppointmentTimeSelectionSection;

export const AnimatedAppointmentTimeSelectionSection = animated(
	StyledAppointmentTimeSelectionSection
);
